/**
 * Crash reporter (RUM).
 *
 * Every browser-side crash the user actually sees is reported once, together
 * with a short reference code that is *also shown to the user* on the error
 * page. A support message then reads "Kennung A3F9K2QX" and maps to exactly one
 * log line, instead of a screenshot of a page that says nothing.
 *
 * Why this exists (KAN-398): a customer sat in front of "Ein Fehler ist
 * aufgetreten" for a whole morning. The page shows no technical text by design
 * and reported nothing home, and the crash happened before his browser made a
 * single API call — so there was no server-side trace of him at all. We could
 * only guess. Reloads were already reported (see page-reload.ts); crashes, the
 * louder signal, were not.
 *
 * Two entry points, both landing on the same endpoint:
 *   - ``reportClientError`` — from the router's error boundary, which is the
 *     case the user complains about.
 *   - ``initClientErrorReporting`` — global ``error`` /
 *     ``unhandledrejection`` handlers, for everything that never reaches a
 *     boundary (crashes before mount, background promises). Registered from
 *     ``client-error-boot.ts``, which ``main.tsx`` imports first.
 *
 * **A reference is only ever handed back when the report actually went out.**
 * Showing a code for a suppressed report would send the user to support with a
 * number nobody can find — the exact dead end this whole change removes.
 *
 * Privacy: error message, stacks, parameterised path (UUIDs replaced, query
 * string dropped) and the build id — never page content. All fields truncated;
 * the backend caps them again.
 */
import { clientBuildId } from '@/lib/build-version';
import { getNativeAuthHeader } from '@/lib/native/token';
import { sanitizePath } from '@/lib/telemetry/slow-requests';

const REPORT_PATH = '/api/v1/client-metrics/errors';

const MAX_PATH_LENGTH = 300;
const MAX_MESSAGE_LENGTH = 500;
const MAX_STACK_LENGTH = 2000;
const MAX_COMPONENT_STACK_LENGTH = 1500;

/**
 * Hard cap per tab. A crash inside a render loop can fire hundreds of times a
 * second, and this endpoint's traffic already rises exactly when the app is
 * unwell — the first few reports carry all the information the later ones do.
 */
const MAX_REPORTS_PER_TAB = 5;

/**
 * Reference alphabet without the pairs people confuse when reading a code out
 * loud or copying it from a screenshot: no 0/O, no 1/I.
 */
const REFERENCE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const REFERENCE_LENGTH = 8;

let reportCount = 0;
/** Fingerprint → the reference of the report that actually went out for it. */
const reportedReferences = new Map<string, string>();

/**
 * React component stacks, keyed by the thrown error.
 *
 * TanStack Router does not pass ``errorInfo`` to the error component — it hands
 * it to ``defaultOnCatch`` and gives the component a hard-coded empty stack. So
 * the router-level hook parks the stack here (see ``main.tsx``) and the report
 * picks it up. Without this the single most useful field is silently always
 * null, which is exactly what it was until QA caught it: minified production
 * stacks make the component stack the only readable part of a crash.
 *
 * Weak on purpose — a stack for an error nobody reports must not keep that
 * error alive.
 */
const componentStacks = new WeakMap<object, string>();

/**
 * Errors the app has already dealt with, although the promise they reject is
 * awaited by nobody. A library that hands an error to our callback and then
 * rethrows it from a promise its own caller drops (assistant-ui's "Erneut
 * generieren", KAN-1816) produces exactly that: the user saw the error where
 * it belongs, and the rejection is no crash.
 */
const handledRejections = new WeakSet<object>();

/**
 * Declare `error` dealt with: should it reject a promise nobody awaits, it is
 * neither reported nor logged as uncaught. Only for errors the caller has shown
 * or recovered from in full.
 */
export function markRejectionHandled(error: unknown): void {
  if (typeof error === 'object' && error !== null) handledRejections.add(error);
}

/** Whether `reason` is an error the app has declared dealt with. */
export function isHandledRejection(reason: unknown): boolean {
  return typeof reason === 'object' && reason !== null && handledRejections.has(reason);
}

/** Test-only: forget this tab's dedupe state and cap between cases. */
export function _resetClientErrorReporterForTests(): void {
  reportCount = 0;
  reportedReferences.clear();
}

/**
 * A short code for one crash, shown to the user and sent with the report.
 * Not a security token — collisions only ever cost a wider Loki search, so
 * ``Math.random`` is an acceptable fallback where ``crypto`` is unavailable.
 */
export function newErrorReference(): string {
  const bytes = new Uint8Array(REFERENCE_LENGTH);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < REFERENCE_LENGTH; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let out = '';
  for (const byte of bytes) {
    out += REFERENCE_ALPHABET[byte % REFERENCE_ALPHABET.length];
  }
  return out;
}

/**
 * Park the React component stack for an error the router just caught, so the
 * report that follows can carry it. Called from the router's ``defaultOnCatch``.
 */
export function rememberComponentStack(error: unknown, componentStack: string | null): void {
  if (!componentStack || typeof error !== 'object' || error === null) return;
  componentStacks.set(error, componentStack);
}

/** Which code path caught the crash. Kebab-case; mirrored in the backend log. */
export type ClientErrorSource =
  /** The router's error boundary — the page the user is looking at. */
  | 'route-error-boundary'
  /** An uncaught error that never reached a boundary. */
  | 'window-error'
  /** A rejected promise nobody handled. */
  | 'unhandled-rejection'
  /** A failed Office.js call in the Office add-in (lib/office/office-telemetry.ts). */
  | 'office-addin';

interface ClientErrorInput {
  source: ClientErrorSource;
  error: unknown;
}

function truncate(value: string | undefined | null, max: number): string | null {
  if (!value) return null;
  return value.length > max ? value.slice(0, max) : value;
}

/** Message and constructor name for anything that can be thrown, not just Errors. */
function describe(error: unknown): { name: string | null; message: string; stack: string | null } {
  if (error instanceof Error) {
    return {
      name: error.name || null,
      message: error.message || String(error),
      stack: error.stack ?? null,
    };
  }
  if (typeof error === 'string') return { name: null, message: error, stack: null };
  // Thrown objects, DOM exceptions, rejected non-Errors — keep whatever shape
  // survives stringification rather than reporting "[object Object]" and no name.
  const asRecord = error as { name?: unknown; message?: unknown; stack?: unknown } | null;
  const message = typeof asRecord?.message === 'string' ? asRecord.message : String(error);
  return {
    name: typeof asRecord?.name === 'string' ? asRecord.name : null,
    message: message || 'Unknown error',
    stack: typeof asRecord?.stack === 'string' ? asRecord.stack : null,
  };
}

/**
 * Report one crash and return the reference to show the user — or ``null`` when
 * nothing was sent, so the caller shows no code rather than an unfindable one.
 *
 * Repeats of a crash already reported in this tab return the *first* report's
 * reference: the code on screen then still matches a log line, however many
 * times the crash re-rendered.
 *
 * Never throws and never awaits: a reporter that can break the error page it
 * runs on is worse than no reporter.
 */
export function reportClientError({ source, error }: ClientErrorInput): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const { name, message, stack } = describe(error);
    // Same crash, same tab: report it once. A render loop would otherwise fill
    // the log with identical lines and drown the next, different crash.
    const fingerprint = `${source}|${name ?? ''}|${message}`;
    const alreadyReported = reportedReferences.get(fingerprint);
    if (alreadyReported) return alreadyReported;
    if (reportCount >= MAX_REPORTS_PER_TAB) return null;

    const reference = newErrorReference();
    reportedReferences.set(fingerprint, reference);
    reportCount += 1;

    const componentStack =
      typeof error === 'object' && error !== null ? componentStacks.get(error) : undefined;

    const payload = {
      reference,
      source,
      // Truncated like every other field: an over-long path used to fail
      // validation and lose the whole report, having spent a slot in the cap.
      path: truncate(sanitizePath(window.location.pathname), MAX_PATH_LENGTH) ?? '/',
      message: truncate(message, MAX_MESSAGE_LENGTH) ?? 'Unknown error',
      name,
      stack: truncate(stack, MAX_STACK_LENGTH),
      componentStack: truncate(componentStack, MAX_COMPONENT_STACK_LENGTH),
      clientBuildId: clientBuildId() ?? null,
      // Time-since-this-document-started: separates "crashed on open" from
      // "crashed after an hour of work" without any extra bookkeeping.
      tabAgeMs: Math.round(performance.now()),
    };

    // Deliberately NOT fetchWithAuth: a background reporter must never trigger
    // the 401 login redirect. Cookies carry the auth on the web, the Bearer
    // session in the app and the Office pane, which have no cookie (without it
    // their reports arrived as anonymous). Errors are swallowed.
    // keepalive so a report survives the reload that may follow immediately.
    void fetch(REPORT_PATH, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', ...getNativeAuthHeader() },
      body: JSON.stringify(payload),
    }).catch(() => {
      // Fire-and-forget.
    });

    if (process.env.NODE_ENV !== 'production') {
      console.info('[ClientErrorReporter]', payload);
    }

    return reference;
  } catch {
    // Reporting a crash must not cause one.
    return null;
  }
}

/**
 * Catch what no error boundary sees: uncaught errors and rejected promises.
 *
 * Registered from ``client-error-boot.ts`` — imported before everything else in
 * ``main.tsx``, because a module that throws while *loading* leaves a blank
 * page and would otherwise be the one crash nobody ever hears about.
 */
export function initClientErrorReporting(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event: ErrorEvent) => {
    // Resource load failures (<script>, <link>, <img>) also fire "error" on
    // window, but only in the capture phase and without an ErrorEvent shape.
    // Those are the bootstrap self-heal's business, not ours.
    if (!event.error && !event.message) return;
    reportClientError({ source: 'window-error', error: event.error ?? event.message });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    // Dealt with already: no report, and no "Uncaught (in promise)" either.
    if (isHandledRejection(event.reason)) {
      event.preventDefault();
      return;
    }
    reportClientError({ source: 'unhandled-rejection', error: event.reason });
  });
}
